import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  AlertCircle,
  BrainCircuit,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleUserRound,
  Clock3,
  FilePenLine,
  ListChecks,
  Loader2,
  MessageSquareText,
  PenLine,
  Send,
  UsersRound,
} from 'lucide-react'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'

import { loansService, type StageDwellSegment } from '@/services/loans'
import { journalService } from '@/services/journal'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { NarrativesPanel } from '@/components/deal/NarrativesPanel'
import { canViewModule } from '@/auth/modulePermission'
import { useSessionPermissions } from '@/auth/useCanEditModule'
import type { DossierWorkspaceModel, JournalEntry } from './workspace/types'
import {
  buildDealTimeline,
  buildJournalCollaborators,
  buildJournalTasks,
  type JournalTaskRow,
} from './journalModel'
import { usePanelExpansion } from './ui'
import { parseDateValue } from '@/utils/dates'

type TaskFilter = 'all' | 'open' | 'overdue' | 'done'

type SentimentSnapshot = {
  positive: number
  neutral: number
  negative: number
  label: string
  asOf: string | null
  /** How many notes, narrative entries and logged communications were read. */
  itemsScored?: number
  method?: string
}

/** A server-computed fact and the record it came from. Plain strings are
 *  accepted for callers that have no source to name. */
export type JournalInsight =
  | string
  | { text: string; source?: string | null; tone?: 'positive' | 'neutral' | 'negative' | null }

type IntelligenceSnapshot = {
  summary: string
  findings: string[]
  asOf: string
}

type JournalPageProps = {
  loanId: string
  model: DossierWorkspaceModel
  segments: readonly StageDwellSegment[]
  noteDisabled?: boolean
  sentiment?: SentimentSnapshot | null
  intelligence?: IntelligenceSnapshot | null
  insights?: readonly JournalInsight[]
  onOpenActivity?: () => void
}

const PANEL = 'rounded-lg border border-amber-300/15 bg-[#0b0b0b] shadow-[0_12px_30px_rgba(0,0,0,0.18)]'

function Panel({
  title,
  icon,
  action,
  className = '',
  children,
}: {
  title: string
  icon: ReactNode
  action?: ReactNode
  className?: string
  children: ReactNode
}) {
  const expand = usePanelExpansion(title)
  return (
    <section className={`dossier-panel ${PANEL} flex min-w-0 flex-col overflow-hidden ${className}`}>
      <header className="flex min-h-8 shrink-0 items-center justify-between gap-3 border-b border-white/[0.06] px-3.5 py-1.5">
        <h3 className="flex min-w-0 items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-300">
          {icon}
          {expand.trigger(title, 'truncate')}
        </h3>
        {action ? <span className="flex shrink-0 items-center gap-2">{action}</span> : null}
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
      {expand.modal(children)}
    </section>
  )
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex min-h-[72px] flex-1 flex-col items-center justify-center px-5 py-3 text-center">
      <AlertCircle size={18} className="mb-2 text-stone-600" />
      <p className="text-[11px] font-medium text-stone-300">{title}</p>
      <p className="mt-1 max-w-sm text-[10px] leading-4 text-stone-600">{detail}</p>
    </div>
  )
}

function stamp(value: string | null | undefined) {
  if (!value) return { date: 'Date not recorded', time: '' }
  // A task's due date is date-only: the day it names, not UTC midnight.
  const parsed = parseDateValue(value)
  if (!parsed) return { date: value, time: '' }
  return {
    date: parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
    time: parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  }
}

function shortActor(value: string | null) {
  if (!value) return 'Actor not recorded'
  return value.includes('@') ? value.split('@')[0].replace(/[._-]+/g, ' ') : value
}

function taskTone(state: JournalTaskRow['state']) {
  if (state === 'done') return 'border-emerald-400/25 bg-emerald-400/[0.06] text-emerald-300'
  if (state === 'overdue') return 'border-red-400/25 bg-red-400/[0.06] text-red-300'
  return 'border-amber-300/25 bg-amber-300/[0.06] text-amber-200'
}

function insightTone(tone: string | null | undefined) {
  if (tone === 'negative') return 'bg-red-400'
  if (tone === 'positive') return 'bg-emerald-400'
  return 'bg-amber-300/70'
}

function SentimentPanel({ sentiment }: { sentiment: SentimentSnapshot | null }) {
  const data = sentiment
    ? [
        { name: 'Positive', value: sentiment.positive, color: '#34d399' },
        { name: 'Neutral', value: sentiment.neutral, color: '#78716c' },
        { name: 'Negative', value: sentiment.negative, color: '#f87171' },
      ]
    : []
  return (
    <Panel title="Sentiment Tracker" icon={<Activity size={13} />}>
      {!sentiment ? (
        <EmptyState
          title="Sentiment is unavailable"
          detail="Read on the server once this deal has at least three notes, narrative entries or logged communications. Nothing is scored in the browser."
        />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(96px,0.8fr)_1fr] items-center gap-3 px-3.5 py-2">
          <div className="relative aspect-square max-h-32 min-h-[88px] min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="68%" outerRadius="96%" stroke="transparent" isAnimationActive={false}>
                  {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-lg font-semibold leading-none text-stone-100">{sentiment.positive}%</span>
              <span className="mt-0.5 text-[9px] text-stone-500">{sentiment.label}</span>
            </div>
          </div>
          <div className="min-w-0 space-y-1.5">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center justify-between gap-3 text-[10px]">
                <span className="flex items-center gap-2 text-stone-400">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                  {entry.name}
                </span>
                <span className="font-mono text-stone-200">{entry.value}%</span>
              </div>
            ))}
            <p className="border-t border-white/[0.05] pt-1.5 text-[8px] leading-3 text-stone-600">
              {sentiment.itemsScored != null ? `${sentiment.itemsScored} notes, narratives & logged comms · ` : ''}
              lexicon count, not a model
              {sentiment.asOf ? ` · as of ${stamp(sentiment.asOf).date}` : ''}
            </p>
          </div>
        </div>
      )}
    </Panel>
  )
}

function ActivityRow({ entry }: { entry: JournalEntry }) {
  const when = stamp(entry.at)
  return (
    <article className="grid min-w-0 grid-cols-[58px_minmax(0,1fr)] border-b border-white/[0.05] last:border-b-0">
      <div className="flex flex-col items-center justify-center border-r border-white/[0.05] px-2 py-3 text-center">
        <span className="text-[9px] uppercase text-stone-600">{when.date.split(' ')[0]}</span>
        <span className="text-base font-medium text-stone-200">{when.date.match(/\d+/)?.[0] ?? '—'}</span>
        <span className="text-[8px] text-stone-600">{when.time}</span>
      </div>
      <div className="min-w-0 px-3 py-2.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] text-stone-500">
              {shortActor(entry.actor)} <span className="mx-1 text-stone-700">·</span> {entry.kind.replace(/[_-]+/g, ' ')}
            </p>
            <h4 className="mt-0.5 truncate text-[11px] font-medium text-amber-200">{entry.title}</h4>
          </div>
          <span className="rounded border border-stone-800 px-2 py-0.5 text-[8px] uppercase tracking-wider text-stone-500">
            {entry.kind.replace(/[_-]+/g, ' ')}
          </span>
        </div>
        {entry.summary ? <p className="mt-1 text-[10px] leading-4 text-stone-400">{entry.summary}</p> : null}
      </div>
    </article>
  )
}

export function JournalPage({
  loanId,
  model,
  segments,
  noteDisabled = false,
  sentiment = null,
  insights = [],
  onOpenActivity,
}: JournalPageProps) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const [noteSaved, setNoteSaved] = useState(false)
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('all')
  const [activityKind, setActivityKind] = useState('all')
  const [activityUser, setActivityUser] = useState('all')
  const [activitySort, setActivitySort] = useState<'recent' | 'oldest'>('recent')
  const [narrativesOpen, setNarrativesOpen] = useState(false)

  // Closing checklists belong to the Closing module. A role without it (the
  // Step-1 analyst) would only get a 403 here, shown as an outage on every deal.
  // Read from the session's own grants, so an Entra session (no local profile)
  // is scoped by /auth/me rather than allowed everything.
  const sessionPermissions = useSessionPermissions()
  const closingInScope = canViewModule(sessionPermissions, 'closing')
  const checklists = useQuery({
    queryKey: ['journal-checklists', loanId],
    queryFn: () => journalService.listChecklists(loanId),
    enabled: Boolean(loanId) && closingInScope,
    retry: false,
  })

  const saveNote = useMutation({
    mutationFn: (body: string) => loansService.addNote(loanId, body),
    onSuccess: (saved) => {
      queryClient.setQueryData<unknown[]>(['loan-notes', loanId], (current) => [
        ...(Array.isArray(current) ? current : []),
        saved,
      ])
      void queryClient.invalidateQueries({ queryKey: ['loan-notes', loanId] })
      void queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.activity(loanId) })
      void queryClient.invalidateQueries({ queryKey: LOAN_QUERY_KEYS.stageAudit(loanId) })
      void queryClient.invalidateQueries({ queryKey: ['journal-intelligence', loanId] })
      setNote('')
      setNoteSaved(true)
    },
  })

  const completeTask = useMutation({
    mutationFn: (task: JournalTaskRow) => {
      if (!task.checklistId || !task.itemId) throw new Error('This action is read-only.')
      return journalService.updateChecklistItem(task.checklistId, task.itemId, 'Received')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['journal-checklists', loanId] })
    },
  })

  const timeline = useMemo(() => buildDealTimeline(segments, model.journal).slice(-6), [segments, model.journal])
  const collaborators = useMemo(
    () => buildJournalCollaborators(model.dealTeam, model.parties),
    [model.dealTeam, model.parties],
  )
  const tasks = useMemo(
    () => buildJournalTasks(checklists.data ?? [], model.obligations, model.nextActions),
    [checklists.data, model.obligations, model.nextActions],
  )
  const shownTasks = taskFilter === 'all' ? tasks : tasks.filter((task) => task.state === taskFilter)
  const taskCounts = {
    all: tasks.length,
    open: tasks.filter((task) => task.state === 'open').length,
    overdue: tasks.filter((task) => task.state === 'overdue').length,
    done: tasks.filter((task) => task.state === 'done').length,
  }

  const activityKinds = useMemo(
    () => [...new Set(model.journal.map((entry) => entry.kind))].sort(),
    [model.journal],
  )
  const activityUsers = useMemo(
    () => [...new Set(model.journal.map((entry) => entry.actor).filter((actor): actor is string => Boolean(actor)))].sort(),
    [model.journal],
  )
  const shownActivity = useMemo(() => {
    const filtered = model.journal.filter((entry) =>
      (activityKind === 'all' || entry.kind === activityKind)
      && (activityUser === 'all' || entry.actor === activityUser),
    )
    return [...filtered].sort((left, right) => {
      const delta = (Date.parse(right.at) || 0) - (Date.parse(left.at) || 0)
      return activitySort === 'recent' ? delta : -delta
    })
  }, [model.journal, activityKind, activityUser, activitySort])

  return (
    // On a desktop screen the three rows share the tab's height and each panel
    // scrolls inside itself, so the page never scrolls. The narrative editor
    // needs real room, so while it is open the page scrolls as before.
    <div
      className={`h-full min-h-0 overflow-y-auto pr-1 text-stone-200 ${
        narrativesOpen ? '' : 'xl:flex xl:flex-col xl:gap-2 xl:overflow-hidden'
      }`}
    >
      {/* Halves, halves, thirds: the panels in each row share a width and a
          height, so the page reads as one grid. Quick note leads because
          writing to the deal is the thing this tab is for. */}
      <div className="grid gap-3 xl:min-h-0 xl:flex-[0.97] xl:grid-cols-2 xl:grid-rows-[minmax(0,1fr)]">        <section className={`dossier-panel ${PANEL} flex min-h-0 min-w-0 flex-col overflow-hidden`} aria-label="Journal composer">
          <div className="flex flex-wrap gap-5 border-b border-white/[0.06] px-4 pt-2">
            {/* Only what saves. Task, Follow Up and Decision tabs sat here
                with no endpoint behind them and a button reading
                "Unavailable"; tasks are recorded on the closing checklist and
                decisions in their own workflows, both listed on this page. */}
            <span className="border-b-2 border-amber-300 pb-2 text-[10px] text-amber-200">Quick Note</span>
            {/* Narratives used to open from the AI Intelligence panel. */}
            <button
              type="button"
              onClick={() => setNarrativesOpen((open) => !open)}
              className="ml-auto pb-2 text-[9px] text-amber-300 hover:text-amber-200"
            >
              {narrativesOpen ? 'Close narratives' : 'Narratives'}
            </button>
          </div>
          <div className="flex min-h-[84px] flex-1 items-stretch gap-3 p-3">
            <textarea
                aria-label="Journal note"
                value={note}
                disabled={noteDisabled || saveNote.isPending}
                onChange={(event) => {
                  setNote(event.target.value)
                  setNoteSaved(false)
                }}
                placeholder={noteDisabled ? 'Unlock the dossier to add a note.' : 'Add a note, update, or next action…'}
                className="min-h-[58px] min-w-0 flex-1 resize-none rounded-md border border-stone-800 bg-black/35 px-3 py-2 text-[10px] leading-4 text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-300/45 disabled:cursor-not-allowed"
              />
            <button
              type="button"
              onClick={() => saveNote.mutate(note.trim())}
              disabled={noteDisabled || !note.trim() || saveNote.isPending}
              className="inline-flex w-28 shrink-0 items-center justify-center gap-1.5 self-center rounded-md border border-amber-300/35 bg-amber-300/[0.08] px-3 py-2 text-[10px] font-medium text-amber-200 hover:bg-amber-300/[0.12] disabled:cursor-not-allowed disabled:border-stone-800 disabled:bg-transparent disabled:text-stone-600"
            >
              {saveNote.isPending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Save Note
            </button>
          </div>
          {saveNote.isError ? <p className="px-3 pb-2 text-[9px] text-red-300">The note could not be saved. Nothing was added.</p> : null}
          {noteSaved ? <p className="px-3 pb-2 text-[9px] text-emerald-300">Note saved to this deal.</p> : null}
        </section>

        <Panel
          title="Deal Timeline"
          icon={<CalendarClock size={13} />}
          action={onOpenActivity ? (
            <button type="button" onClick={onOpenActivity} className="flex items-center gap-1 text-[9px] text-amber-300 hover:text-amber-200">
              Full timeline <ChevronRight size={11} />
            </button>
          ) : undefined}
        >
          {timeline.length === 0 ? (
            <EmptyState title="No timeline events recorded" detail="Stage history, notes, and deal activity will appear here when captured." />
          ) : (
            <ol className="px-4 py-2">
              {timeline.map((entry, index) => {
                const when = stamp(entry.at)
                return (
                  <li key={entry.id} className="grid grid-cols-[14px_minmax(112px,0.7fr)_minmax(0,1.3fr)] gap-3 py-1.5">
                    <div className="relative flex justify-center">
                      <span className="relative z-10 mt-1 h-2.5 w-2.5 rounded-full border border-amber-200 bg-amber-500/70" />
                      {index < timeline.length - 1 ? <span className="absolute bottom-[-14px] top-3 w-px bg-amber-300/35" /> : null}
                    </div>
                    <div className="text-[9px] leading-4 text-stone-500">
                      <div>{when.date}</div>
                      <div>{when.time}</div>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-[10px] font-medium text-stone-200">{entry.title}</p>
                      <p className="mt-0.5 line-clamp-2 text-[9px] leading-4 text-stone-500">
                        {entry.detail ?? (entry.actor ? `Recorded by ${entry.actor}` : 'No additional detail recorded.')}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ol>
          )}
        </Panel>

      </div>

      <div className="mt-3 grid gap-3 xl:mt-0 xl:min-h-0 xl:flex-[1.07] xl:grid-cols-2 xl:grid-rows-[minmax(0,1fr)]">        <Panel title="Tasks & Actions" icon={<ListChecks size={13} />}>
          <div className="flex flex-wrap gap-1.5 border-b border-white/[0.05] px-3 py-2">
            {(['all', 'open', 'overdue', 'done'] as const).map((filter) => (
              <button
                type="button"
                key={filter}
                onClick={() => setTaskFilter(filter)}
                className={`rounded-full border px-2 py-1 text-[8px] capitalize ${
                  taskFilter === filter
                    ? 'border-amber-300/45 bg-amber-300/[0.08] text-amber-200'
                    : 'border-stone-800 text-stone-500'
                }`}
              >
                {filter} ({taskCounts[filter]})
              </button>
            ))}
          </div>
          {shownTasks.length === 0 ? (
            <EmptyState
              title={!closingInScope
                ? 'No tasks in this view'
                : checklists.isError ? 'Checklist data is unavailable' : 'No tasks in this view'}
              detail={!closingInScope
                ? 'Closing checklists are not part of your role. Recorded deal conditions and next actions appear here.'
                : checklists.isError
                  ? 'The closing-checklist API could not be read. Recorded deal conditions still appear when available.'
                  : 'No closing-checklist item or deal condition matches this filter.'}
            />
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {shownTasks.slice(0, 8).map((task) => (
                <div key={task.id} className="flex items-start gap-2.5 px-3 py-2.5">
                  {task.state === 'done'
                    ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-400" />
                    : task.state === 'overdue'
                      ? <AlertCircle size={14} className="mt-0.5 shrink-0 text-red-400" />
                      : <Clock3 size={14} className="mt-0.5 shrink-0 text-amber-300" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] leading-4 text-stone-300">{task.label}</p>
                    <p className="mt-0.5 text-[8px] text-stone-600">
                      {[task.dueDate ? `Due ${stamp(task.dueDate).date}` : null, task.owner].filter(Boolean).join(' · ') || 'No owner or due date recorded'}
                    </p>
                  </div>
                  {task.source === 'checklist' && task.state !== 'done' ? (
                    <button
                      type="button"
                      aria-label={`Mark ${task.label} received`}
                      onClick={() => completeTask.mutate(task)}
                      disabled={completeTask.isPending}
                      className="rounded border border-stone-800 px-1.5 py-1 text-[8px] text-stone-500 hover:border-emerald-400/30 hover:text-emerald-300"
                    >
                      Mark received
                    </button>
                  ) : (
                    <span className={`rounded border px-1.5 py-0.5 text-[8px] capitalize ${taskTone(task.state)}`}>{task.state}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Activity Journal"
          icon={<MessageSquareText size={13} />}
          action={
            <>
              {/* Filters sit in the header so the entries get the height. */}
              <select aria-label="Activity type" value={activityKind} onChange={(event) => setActivityKind(event.target.value)} className="rounded border border-stone-800 bg-black px-2 py-0.5 text-[8px] text-stone-400">
                <option value="all">All activity</option>
                {activityKinds.map((kind) => <option key={kind} value={kind}>{kind.replace(/[_-]+/g, ' ')}</option>)}
              </select>
              <select aria-label="Activity user" value={activityUser} onChange={(event) => setActivityUser(event.target.value)} className="rounded border border-stone-800 bg-black px-2 py-0.5 text-[8px] text-stone-400">
                <option value="all">All users</option>
                {activityUsers.map((user) => <option key={user} value={user}>{user}</option>)}
              </select>
              <select aria-label="Activity order" value={activitySort} onChange={(event) => setActivitySort(event.target.value as 'recent' | 'oldest')} className="rounded border border-stone-800 bg-black px-2 py-0.5 text-[8px] text-stone-400">
                <option value="recent">Most recent</option>
                <option value="oldest">Oldest first</option>
              </select>
              {onOpenActivity ? (
                <button type="button" onClick={onOpenActivity} className="flex items-center gap-1 text-[9px] text-amber-300 hover:text-amber-200">
                  View all <ChevronRight size={11} />
                </button>
              ) : null}
            </>
          }
        >
          {shownActivity.length === 0 ? (
            <EmptyState title="No journal activity recorded" detail="Real loan notes and auditable deal events will appear here." />
          ) : (
            <div>{shownActivity.slice(0, 8).map((entry) => <ActivityRow key={entry.id} entry={entry} />)}</div>
          )}
        </Panel>
      </div>

      <div className="mt-3 grid gap-3 xl:mt-0 xl:min-h-0 xl:flex-[0.68] xl:grid-cols-3 xl:grid-rows-[minmax(0,1fr)]">
        <SentimentPanel sentiment={sentiment} />
        <Panel title="Collaborators" icon={<UsersRound size={13} />}>
          {collaborators.length === 0 ? (
            <EmptyState title="No collaborators recorded" detail="Deal-team and party records will appear here. Presence and availability are not tracked." />
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {collaborators.slice(0, 8).map((person) => (
                <div key={person.id} className="flex items-center gap-2.5 px-3 py-1.5">
                  <CircleUserRound size={20} className="shrink-0 text-amber-300/70" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[10px] text-stone-200">{person.name}</p>
                    <p className="truncate text-[8px] text-stone-600">{person.role} · {person.relationship}</p>
                  </div>
                  <span className="text-[8px] text-stone-700">Presence not tracked</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Recent Insights" icon={<BrainCircuit size={13} />}>
          {insights.length === 0 ? (
            <EmptyState title="No governed insights stored" detail="Insights are computed on the server from this deal's conditions, documents, covenants and model comparison — never inferred from notes or market data." />
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {insights.map((insight, index) => {
                const item = typeof insight === 'string' ? { text: insight } : insight
                return (
                  <li key={`${index}-${item.text}`} className="flex items-start gap-2 px-3 py-1.5">
                    <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${insightTone(item.tone)}`} aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] leading-4 text-stone-300">{item.text}</p>
                      {item.source ? (
                        <p className="text-[8px] uppercase tracking-[0.12em] text-stone-600">{item.source}</p>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </Panel>
      </div>

      {narrativesOpen ? (
        <Panel
          title="Narrative Editor"
          icon={<FilePenLine size={13} />}
          className="mt-3"
          action={<button type="button" onClick={() => setNarrativesOpen(false)} className="flex items-center gap-1 text-[9px] text-stone-500 hover:text-stone-300"><PenLine size={11} /> Close</button>}
        >
          <div className="min-h-[340px] p-3">
            <NarrativesPanel dealId={loanId} readOnly={noteDisabled} />
          </div>
        </Panel>
      ) : null}
    </div>
  )
}

export default JournalPage
